import { element as el } from '../dom.js';
import type { GameContent } from '../../../shared/game/world-types.js';
import type { WorldSnapshot } from '../../../shared/network/protocol.js';

/**
 * The parchment page a reading opens on. The server decides what was read (a hero's `reading` in the snapshot); this opens the
 * page for the first new reading by one of this player's heroes. The world keeps running behind it.
 */
export class ReadingPanel {
  private seen = new Map<string, number>();
  private primed = false;
  private readonly root = el<HTMLDialogElement>('reading-panel');

  constructor() {
    el('reading-close').addEventListener('click', () => this.close());
    this.root.addEventListener('pointerdown', event => { if (event.target === this.root) this.close(); });
    this.root.addEventListener('cancel', event => { event.preventDefault(); this.close(); });
  }

  get open() { return this.root.open; }
  close() { if (this.root.open) this.root.close(); }
  /** Forget what was read before a world is entered, so an old reading is not shown again. */
  reset() { this.seen.clear(); this.primed = false; this.close(); }

  receive(snapshot: WorldSnapshot, content: GameContent, owned: readonly string[]) {
    let fresh: string | undefined;
    for (const hero of snapshot.heroes) {
      const serial = hero.reading?.serial ?? 0;
      if (serial > (this.seen.get(hero.id) ?? 0)) {
        this.seen.set(hero.id, serial);
        if (this.primed && owned.includes(hero.id)) fresh = hero.reading!.readableId;
      }
    }
    this.primed = true;
    const readable = fresh === undefined ? undefined : content.readables?.find(candidate => candidate.id === fresh);
    if (readable) this.show(readable.title, readable.paragraphs, readable.signature);
  }

  private show(title: string, paragraphs: readonly string[], signature?: string) {
    el('reading-title').textContent = title;
    el('reading-body').replaceChildren(...paragraphs.map(text => Object.assign(document.createElement('p'), { textContent: text })));
    const sign = el('reading-signature'); sign.textContent = signature ?? ''; sign.hidden = !signature;
    // A modal dialog stands above the pack when a page is read from it.
    if (!this.root.open) this.root.showModal();
    el('reading-body').scrollTop = 0; el('reading-page').focus({ preventScroll: true });
  }
}
